/**
 * RentalsTab - Rentals management tab component with subscription status
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  RefreshCw, 
  AlertCircle,
  Loader2,
  Search,
  Eye,
  User,
  ArrowRight,
  CreditCard,
  AlertTriangle,
  Clock,
  CheckCircle,
  PauseCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import { API, formatEST } from "./utils";

export default function RentalsTab({ onUserClick, onRentalClick }) {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [statuses, setStatuses] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  
  // View mode: 'active' or 'all' (including archived)
  const [viewMode, setViewMode] = useState('active');
  const [allRentals, setAllRentals] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const fetchRentals = async () => {
    setLoading(true);
    setError(null);
    try {
      // First, load rentals quickly from gantt endpoint (no Stripe calls)
      const response = await axios.get(`${API}/poles/gantt`);
      const data = response.data;
      
      // Group by rental_id to get unique rentals
      const rentalsMap = {};
      (data.rentals || []).forEach(rental => {
        if (!rental.rental_id) return; // Skip available poles
        if (!rentalsMap[rental.rental_id]) {
          rentalsMap[rental.rental_id] = {
            rental_id: rental.rental_id,
            user_id: rental.user_id,
            renter_name: rental.renter_name,
            status_name: rental.custom_status,
            start_date: rental.start_date,
            return_date: rental.end_date,
            poles: [],
            subscription_status: null // Will be loaded later
          };
        }
        rentalsMap[rental.rental_id].poles.push({
          pole_id: rental.pole_id,
          pole_code: rental.pole_code,
          length: rental.length,
          weight: rental.weight,
          brand: rental.brand
        });
      });
      
      // Convert to array and sort by rental_id descending
      const rentalsArray = Object.values(rentalsMap).sort((a, b) => b.rental_id - a.rental_id);
      setRentals(rentalsArray);
      
      // Get unique statuses
      const statusSet = new Set(rentalsArray.map(r => r.status_name));
      setStatuses([...statusSet].sort());
      setLoading(false);
      
      // Now load subscription status in background (don't block UI)
      fetchSubscriptionStatus(rentalsArray);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error("Failed to load rentals");
      setLoading(false);
    }
  };

  // Load subscription status separately (non-blocking)
  const fetchSubscriptionStatus = async (rentalsArray) => {
    if (rentalsArray.length === 0) return;
    
    setLoadingSubscriptions(true);
    try {
      const response = await axios.get(`${API}/rentals/all/with-subscriptions`, {
        timeout: 30000 // 30 second timeout
      });
      const subscriptionData = response.data.rentals || [];
      
      // Create a map of rental_id -> subscription_status
      const subStatusMap = {};
      subscriptionData.forEach(r => {
        subStatusMap[r.rental_id] = {
          subscription_status: r.subscription_status,
          subscription_id: r.subscription_id
        };
      });
      
      // Update rentals with subscription status
      setRentals(prev => prev.map(rental => ({
        ...rental,
        subscription_status: subStatusMap[rental.rental_id]?.subscription_status || null,
        subscription_id: subStatusMap[rental.rental_id]?.subscription_id || null
      })));
    } catch (err) {
      console.warn("Failed to load subscription status:", err.message);
      // Don't show error to user - subscriptions are optional enhancement
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => { fetchRentals(); }, []);
  
  // Fetch all rentals including archived when switching to 'all' mode
  const fetchAllRentals = async () => {
    setLoadingAll(true);
    try {
      const response = await axios.get(`${API}/rentals/all/including-archived`, {
        params: {
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });
      setAllRentals(response.data.rentals || []);
      setAllStatuses(response.data.statuses || []);
    } catch (err) {
      toast.error("Failed to load archived rentals");
    } finally {
      setLoadingAll(false);
    }
  };
  
  // Fetch all rentals when switching to 'all' mode or when search/filter changes
  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllRentals();
    }
  }, [viewMode, searchTerm, statusFilter]);

  // Filter rentals
  const filteredRentals = useMemo(() => {
    let filtered = rentals;
    
    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status_name === statusFilter);
    }
    
    // Filter by subscription status
    if (subscriptionFilter !== "all") {
      if (subscriptionFilter === "none") {
        filtered = filtered.filter(r => !r.subscription_status);
      } else {
        filtered = filtered.filter(r => r.subscription_status === subscriptionFilter);
      }
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(r => {
        const searchFields = [
          r.renter_name,
          String(r.rental_id),
          r.status_name,
          ...(r.poles || []).map(p => `${p.pole_code} ${p.length} ${p.weight}`)
        ];
        return searchFields.some(field => (field || '').toLowerCase().includes(searchLower));
      });
    }
    
    return filtered;
  }, [rentals, statusFilter, subscriptionFilter, searchTerm]);

  const getStatusConfig = (status) => {
    const configs = {
      'Active': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
      'Awaiting Pickup': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
      'Pole Requested': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
      'Broken': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
      'Completed': { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
      'Cancelled': { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
    };
    return configs[status] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
  };
  
  // Determine which rentals to display based on view mode
  const displayRentals = viewMode === 'all' ? allRentals : filteredRentals;
  const displayStatuses = viewMode === 'all' ? allStatuses : statuses;
  const isLoading = viewMode === 'all' ? loadingAll : loading;

  const getSubscriptionBadge = (subscriptionStatus) => {
    // Show loading state while fetching subscription status
    if (loadingSubscriptions && subscriptionStatus === null) {
      return (
        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-400 border-slate-200">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Loading...
        </Badge>
      );
    }
    
    if (!subscriptionStatus) {
      return (
        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200">
          <XCircle className="w-3 h-3 mr-1" />
          No Subscription
        </Badge>
      );
    }
    
    const configs = {
      'active': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle, label: 'Paying' },
      'trialing': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Clock, label: 'Trial' },
      'past_due': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Past Due' },
      'paused': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: PauseCircle, label: 'Paused' },
      'canceled': { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: XCircle, label: 'Canceled' },
      'unpaid': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Unpaid' },
    };
    
    const config = configs[subscriptionStatus] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: CreditCard, label: subscriptionStatus };
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={`text-xs ${config.bg} ${config.text} border ${config.border}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-slate-600">{error}</p>
        <Button onClick={fetchRentals} variant="outline" size="sm" className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <Button
          variant={viewMode === 'active' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => { setViewMode('active'); setStatusFilter('all'); }}
          className={viewMode === 'active' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          Active Rentals
        </Button>
        <Button
          variant={viewMode === 'all' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => { setViewMode('all'); setStatusFilter('all'); }}
          className={viewMode === 'all' ? 'bg-slate-700 hover:bg-slate-800' : ''}
        >
          All Rentals (Archive)
        </Button>
      </div>
      
      {/* Header - Mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <h2 className="text-base sm:text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {viewMode === 'active' ? 'Active Rentals' : 'All Rentals (Including Archived)'}
          </h2>
          
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search rentals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-full sm:w-[180px] text-sm h-9"
              data-testid="rentals-search"
            />
          </div>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="rentals-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {displayStatuses.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Subscription Filter - Only show in active mode */}
          {viewMode === 'active' && (
            <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
              <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="rentals-subscription-filter">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment</SelectItem>
                <SelectItem value="active">Paying</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="none">No Subscription</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <Badge variant="outline" className="font-mono text-xs w-fit">
            {displayRentals.length} rental{displayRentals.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        
        <Button variant="outline" size="sm" onClick={() => viewMode === 'all' ? fetchAllRentals() : fetchRentals()} className="w-fit h-8">
          <RefreshCw className={`w-4 h-4 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Loading state for All Rentals */}
      {isLoading && viewMode === 'all' ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : displayRentals.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          {searchTerm || statusFilter !== "all" ? 'No rentals match your search' : 'No rentals found'}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {displayRentals.map(rental => {
              const rentalId = rental.rental_id || rental.id;
              const statusName = rental.status_name || rental.status;
              const config = getStatusConfig(statusName);
              return (
                <Link 
                  key={rentalId} 
                  to={`/rentals/${rentalId}`}
                  className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-mono font-bold text-slate-900">#{rentalId}</span>
                      <p className="text-sm text-slate-600 mt-0.5">{rental.renter_name || 'Unknown'}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`${config.bg} ${config.text} border ${config.border} text-xs`}>
                        {statusName}
                      </Badge>
                      {viewMode === 'active' && getSubscriptionBadge(rental.subscription_status)}
                    </div>
                  </div>
                  {rental.poles ? (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {rental.poles.slice(0, 2).map((pole, idx) => (
                        <a 
                          key={idx}
                          href={`/poles?search=${pole.pole_code || pole.pole_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:opacity-80"
                        >
                          <Badge variant="outline" className="text-xs font-mono cursor-pointer hover:bg-slate-100 hover:border-blue-300">
                            {pole.length} - {pole.weight}
                          </Badge>
                        </a>
                      ))}
                      {rental.poles.length > 2 && (
                        <Badge variant="outline" className="text-xs">+{rental.poles.length - 2}</Badge>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-xs mb-2">{rental.pole_count || 0} pole(s)</Badge>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{formatEST(rental.start_date || rental.created_at, 'MMM d, yyyy')}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span>{rental.return_date ? formatEST(rental.return_date, 'MMM d, yyyy') : 'Ongoing'}</span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Rental ID</TableHead>
                  <TableHead>Renter</TableHead>
                  <TableHead>Status</TableHead>
                  {viewMode === 'active' && <TableHead>Payment</TableHead>}
                  <TableHead>Poles</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Return Date</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRentals.map(rental => {
                  const rentalId = rental.rental_id || rental.id;
                  const statusName = rental.status_name || rental.status;
                  const config = getStatusConfig(statusName);
                  return (
                    <TableRow 
                      key={rentalId} 
                      className="hover:bg-slate-50 cursor-pointer" 
                      data-testid={`rental-row-${rentalId}`}
                      onClick={() => navigate(`/rentals/${rentalId}`)}
                    >
                      <TableCell className="font-mono font-medium">
                        <span className="hover:text-blue-600 hover:underline">
                          #{rentalId}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {rental.user_id ? (
                          <Link to={`/rentals/${rentalId}`} className="cursor-pointer hover:text-blue-600 hover:underline">
                            <div className="font-medium">{rental.renter_name || '-'}</div>
                          </Link>
                        ) : (
                          <div className="font-medium text-slate-500">{rental.renter_name || '-'}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${config.bg} ${config.text} border ${config.border}`}>
                          {statusName}
                        </Badge>
                      </TableCell>
                      {viewMode === 'active' && (
                        <TableCell>
                          {getSubscriptionBadge(rental.subscription_status)}
                        </TableCell>
                      )}
                      <TableCell>
                        {rental.poles ? (
                          <div className="flex flex-wrap gap-1">
                            {rental.poles.slice(0, 2).map((pole, idx) => (
                              <a 
                                key={idx}
                                href={`/poles?search=${pole.pole_code || pole.pole_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="hover:opacity-80"
                              >
                                <Badge variant="outline" className="text-xs font-mono cursor-pointer hover:bg-slate-100 hover:border-blue-300">
                                  {pole.length} - {pole.weight}
                                </Badge>
                              </a>
                            ))}
                            {rental.poles.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{rental.poles.length - 2}</Badge>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs">{rental.pole_count || 0} pole(s)</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatEST(rental.start_date || rental.created_at, 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {rental.return_date ? formatEST(rental.return_date, 'MMM d, yyyy') : <span className="text-slate-400">Not set</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Link 
                            to={`/rentals/${rentalId}`}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`view-rental-${rentalId}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          {rental.user_id && (
                            <Link 
                              to={`/users/${rental.user_id}`}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-blue-50 text-slate-500 hover:text-blue-600"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`view-user-${rentalId}`}
                            >
                              <User className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
